import { captureException } from "@/lib/sentry";
export interface ArtistAvailability {
  status: "booked" | "limited" | "available";
  nextAvailable?: string;
  note?: string;
}

export interface ArtistWithPortfolio {
  id: number;
  slug: string;
  name: string;
  role: string;
  image: string;
  detailImage?: string;
  heroImages?: string[];
  heroImagePositions?: Record<string, string>;
  heroImageAdjustments?: Record<string, { brightness?: number; contrast?: number; rotate?: number; zoom?: number }>;
  bio: string;
  instagram: string;
  specialties: string[];
  serviceTypes?: string[];
  tattooingSince: number;
  tattooingSinceMonth?: number | null;
  accentColor: string;
  hourlyMin?: number;
  hourlyMax?: number;
  isActive?: boolean;
  availability?: ArtistAvailability;
  portfolio: { id: number; src: string; alt: string }[];
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function getExperienceLabel(sinceYear: number, sinceMonth?: number | null): string {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1–12

  if (sinceMonth) {
    let years = currentYear - sinceYear;
    if (currentMonth < sinceMonth) years--;
    const monthName = MONTH_NAMES[sinceMonth - 1];
    return `Tattooing since ${monthName} ${sinceYear} · ${years} years`;
  }

  const years = currentYear - sinceYear;
  return `Tattooing since ${sinceYear} · ${years}+ years`;
}

export const ARTISTS_FALLBACK: ArtistWithPortfolio[] = [
  {
    id: 1,
    slug: "kathleen",
    name: "Kathleen",
    role: "Fine Line, Realism & Geometric",
    image: "/storage/artists/kathleen_mg5795.webp",
    detailImage: "/storage/images/eimg_3320_kathleen.webp",
    heroImages: [
      "/storage/artists/kathleen_mg5795.webp",
      "/storage/images/eimg_3320_kathleen.webp",
      "/storage/images/kathleen_hero_1.webp",
      "/storage/images/kathleen_hero_2.webp",
    ],
    heroImagePositions: {},
    bio: "With over 25 years of experience, Kathleen specializes in fine line work, realism, and geometric designs. Her precise technique and eye for detail bring every piece to life.",
    instagram: "@mindmagiktattoo", // @clone-ok artist-default-handle
    specialties: ["Fine Line", "Realism", "Geometric"],
    serviceTypes: ["Tattoo", "Piercing"],
    accentColor: "#a78bfa",
    tattooingSince: 2000,
    tattooingSinceMonth: null,
    hourlyMin: 150,
    hourlyMax: 200,
    isActive: true,
    portfolio: [],
  },
  {
    id: 2,
    slug: "constance-masters",
    name: "Constance",
    role: "Traditional & Neo Traditional",
    image: "/storage/images/constance_hero_3.webp",
    heroImages: ["/storage/images/constance_hero_3.webp"],
    heroImagePositions: {},
    bio: "Constance brings bold lines and rich color to every piece. Her traditional and neo traditional work honors the craft's heritage while pushing creative boundaries.",
    instagram: "@ntc_tattoos",
    specialties: ["Traditional", "Neo Traditional"],
    accentColor: "#fb923c",
    tattooingSince: 2022,
    tattooingSinceMonth: null,
    hourlyMin: 150,
    hourlyMax: 200,
    isActive: true,
    portfolio: [],
  },
  {
    id: 3,
    slug: "mikayla-alderton",
    name: "Mikayla",
    role: "Black & Grey",
    image: "/storage/images/mikayla_profile.jpg",
    heroImages: [
      "/storage/images/mikayla_profile.jpg",
      "/storage/images/mikayla_hero_2.webp",
      "/storage/images/img_4847.webp",
      "/storage/images/img_4848.webp",
    ],
    heroImagePositions: {},
    bio: "Mikayla brings depth and dimension to every piece with her black and grey work. Her smooth shading and attention to detail create stunning results.",
    instagram: "@inked.by_kayla",
    specialties: ["Black & Grey"],
    accentColor: "#94a3b8",
    tattooingSince: 2024,
    tattooingSinceMonth: null,
    hourlyMin: 150,
    hourlyMax: 200,
    isActive: true,
    portfolio: [],
  },
];

export function getAllArtists(apiArtists?: ArtistWithPortfolio[]): ArtistWithPortfolio[] {
  if (apiArtists && apiArtists.length > 0) {
    return apiArtists.map((a) => {
      const fallback = ARTISTS_FALLBACK.find((f) => f.slug === a.slug);
      return {
        ...a,
        tattooingSince: a.tattooingSince || new Date().getFullYear(),
        heroImages: a.heroImages && a.heroImages.length > 0 ? a.heroImages : fallback?.heroImages || [],
        image: a.image || fallback?.image || "",
      };
    });
  }
  return ARTISTS_FALLBACK;
}

export function getArtistBySlug(slug: string, apiArtists?: ArtistWithPortfolio[]): ArtistWithPortfolio | undefined {
  return getAllArtists(apiArtists).find((a) => a.slug === slug);
}

export async function fetchArtistsFromApi(): Promise<ArtistWithPortfolio[]> {
  const res = await fetch("/api/artists");
  if (!res.ok) {
    const _fetchErr = new Error("Failed to fetch artists");
    captureException(_fetchErr);
    throw _fetchErr;
  }
  return res.json();
}
